/**
 * Facturación: la galería de consultorios con sus comprobantes (CB-14).
 *
 * Hasta ahora esta pantalla solo listaba `invoice_requests` —solicitudes que
 * el sistema le hace al emisor externo, 0 filas— y su estado vacío decía
 * «Todavía no hay facturas solicitadas» mientras la base guarda 174
 * comprobantes reales con su archivo. Literalmente cierto y operativamente
 * falso: la matriz anual, dos pestañas más allá, muestra cientos de celdas
 * facturadas (el patrón de estado vacío engañoso que CB-12 ya señaló).
 *
 * Ahora la pantalla es la galería de esos comprobantes: una card por
 * consultorio (el número como identidad, mismo idioma que la galería de
 * clientes) y, adentro, las facturas agrupadas por EL MES QUE CUBREN — no por
 * fecha de emisión, que erró en el 68% de los casos (la lección más cara de
 * `PROCEDIMIENTO-CONCILIACION.md`). Las solicitudes del sistema no
 * desaparecen: quedan como sección aparte cuando existan.
 *
 * Decisiones del dueño que esta pantalla implementa tal cual:
 *   · P2.3 — los 35/24 consultorios son un FILTRO; abre en los 24 con facturas.
 *   · P2.2 — una factura multi-mes aparece en TODOS sus meses, marcada como
 *     compartida, y el monto cuenta UNA vez en los totales.
 *   · P2.1 — el filtro «por doctor» trabaja sobre los tres ejes (receptor /
 *     responsable del período / quien pagó), seleccionables por separado; y
 *     receptor ≠ responsable se señala como anomalía, no como un caso más.
 *   · CB-26/26 bis — los 6 pares NC→FE del mismo día se muestran resueltos
 *     (el mes queda facturado); las otras 6 notas quedan visibles como
 *     «pendiente de revisión» y nada afirma que esos meses estén facturados.
 */

import { useState } from 'react';
import {
  dateLabel, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, periodLabel, periodLabelShort,
  type InvoiceStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Badge, Button, Card, Cell, Empty, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';
import { InvoiceModal, InvoiceTable, type InvoiceRow } from './InvoiceHistory';
import '@/styles/facturacion.css';

/* ── Tipos que llegan del Worker ──────────────────────────────────────────── */

interface GalleryUnit {
  id: string; unit_code: string; name: string | null; status: string;
  invoice_count: number; credit_note_count: number; nc_pending_count: number;
  no_period_count: number; total: string; last_period: string | null;
}

/** La fila del endpoint de galería: la base de `InvoiceRow` más lo que esta
 *  pantalla anota — anulaciones, anomalía y los tres ejes del filtro. */
interface GalleryInvoice extends InvoiceRow {
  is_credit_note: boolean;
  annulled_by: string | null;
  annul_reason: string | null;
  annuls: string | null;
  review_pending: boolean;
  suspect_by: string | null;
  responsables: string[];
  pagadores: { name: string; source: 'sender' | 'party' }[];
  anomaly: 'COINCIDE' | 'DIFIERE' | 'SIN_DATO';
}

interface UnitInvoicesData {
  unit: { id: string; unit_code: string; name: string | null };
  invoices: GalleryInvoice[];
  total: string;
}

interface SystemInvoice {
  id: string; status: InvoiceStatus; consecutivo: string | null; clave_numerica: string | null;
  issued_at: string | null; error_message: string | null; attempt_count: number;
  billing_name_snapshot: string; due_minor: number; unit_code: string; period_start: string;
  created_at: string;
}

/* ── La pantalla ──────────────────────────────────────────────────────────── */

export function InvoicingPage() {
  const [openUnit, setOpenUnit] = useState<GalleryUnit | null>(null);

  return openUnit
    ? <UnitInvoices unit={openUnit} onBack={() => setOpenUnit(null)} />
    : <InvoicingGallery onOpen={setOpenUnit} />;
}

/* ── Nivel 1: la galería de consultorios ─────────────────────────────────── */

function InvoicingGallery({ onOpen }: { onOpen: (u: GalleryUnit) => void }) {
  const query = useApi<{ units: GalleryUnit[] }>('/admin/invoicing/units');
  // P2.3: abre en la galería limpia — solo los consultorios con facturas.
  const [showAll, setShowAll] = useState(false);

  return (
    <>
      <PageHead
        title="Facturación"
        subtitle="Los comprobantes emitidos a cada consultorio, por el mes que cubren. El comprobante legal es el XML."
      />

      <Async query={query}>
        {({ units }) => {
          const withInvoices = units.filter((u) => u.invoice_count + u.credit_note_count > 0);
          const shown = showAll ? units : withInvoices;

          return (
            <>
              <div
                className="segmented mb-3"
                role="group"
                aria-label="Filtrar consultorios"
                style={{ '--seg-n': 2, '--seg-i': showAll ? 1 : 0 } as React.CSSProperties}
              >
                <div className="segmented__pill" />
                <button
                  type="button"
                  aria-pressed={!showAll}
                  className={`segmented__opt${!showAll ? ' is-active' : ''}`}
                  onClick={() => { if (showAll) { haptic.light(); setShowAll(false); } }} // HAPTIC_FEEDBACK
                >
                  Con facturas
                  <span className="segmented__count">{withInvoices.length}</span>
                </button>
                <button
                  type="button"
                  aria-pressed={showAll}
                  className={`segmented__opt${showAll ? ' is-active' : ''}`}
                  onClick={() => { if (!showAll) { haptic.light(); setShowAll(true); } }} // HAPTIC_FEEDBACK
                >
                  Todos
                  <span className="segmented__count">{units.length}</span>
                </button>
              </div>

              {shown.length === 0 ? (
                <Empty
                  icon="documento"
                  title="Sin comprobantes registrados"
                  hint="Cuando se importe o emita una factura, este es su lugar."
                />
              ) : (
                <div className="unit-grid">
                  {shown.map((u) => (
                    <InvoicingCard key={u.id} unit={u} onOpen={() => onOpen(u)} />
                  ))}
                </div>
              )}

              <SystemRequests />
            </>
          );
        }}
      </Async>
    </>
  );
}

/**
 * La card del consultorio. Reusa la clase `.unit-card` —el idioma visual de la
 * galería de clientes: número primero, línea de agua, campo de color— con su
 * propio contenido: cuántos comprobantes hay, hasta qué mes, y las alertas que
 * no pueden esconderse (notas de crédito pendientes, facturas sin período).
 */
function InvoicingCard({ unit, onOpen }: { unit: GalleryUnit; onOpen: () => void }) {
  const empty = unit.invoice_count + unit.credit_note_count === 0;
  return (
    <button type="button" className="unit-card invgal-card" onClick={onOpen}>
      <div className="unit-card__head">
        <span className="unit-card__code">Consultorio {unit.unit_code}</span>
        {unit.nc_pending_count > 0 && (
          <Badge tone="warn" title="Notas de crédito sin resolver: sus meses no se afirman facturados">
            {unit.nc_pending_count} NC pendiente{unit.nc_pending_count === 1 ? '' : 's'}
          </Badge>
        )}
      </div>

      {empty ? (
        // Explícito, no un hueco: el consultorio existe y no tiene comprobantes.
        <div className="unit-card__where">Sin comprobantes registrados</div>
      ) : (
        <>
          <div className="unit-card__where">
            {unit.invoice_count} factura{unit.invoice_count === 1 ? '' : 's'}
            {unit.credit_note_count > 0 && ` · ${unit.credit_note_count} NC`}
            {unit.last_period && ` · hasta ${periodLabelShort(unit.last_period)}`}
          </div>
          <div className="invgal-card__total">
            <Money value={unit.total} />
            <span className="invgal-card__total-label">facturado vigente</span>
          </div>
          {unit.no_period_count > 0 && (
            <div className="invgal-card__pending small">
              {unit.no_period_count} sin período identificado
            </div>
          )}
        </>
      )}
    </button>
  );
}

/* ── Nivel 2: los comprobantes de un consultorio ─────────────────────────── */

/** Los tres ejes de P2.1, con su etiqueta. El orden es el de la decisión. */
const AXES = [
  { key: 'receptor', label: 'Receptor' },
  { key: 'responsable', label: 'Responsable del período' },
  { key: 'pago', label: 'Quien pagó' },
] as const;
type AxisKey = (typeof AXES)[number]['key'];

type TypeFilter = 'ALL' | 'FE' | 'NC';

function UnitInvoices({ unit, onBack }: { unit: GalleryUnit; onBack: () => void }) {
  const query = useApi<UnitInvoicesData>(`/admin/invoicing/units/${unit.id}`);
  const [viewing, setViewing] = useState<InvoiceRow | null>(null);
  const [doctor, setDoctor] = useState('');
  const [axes, setAxes] = useState<Record<AxisKey, boolean>>({
    receptor: true, responsable: true, pago: true,
  });
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');

  return (
    <>
      <PageHead
        title={`Consultorio ${unit.unit_code}`}
        subtitle="Comprobantes por el mes que cubren. El mes manda sobre la fecha de emisión."
        actions={<Button onClick={onBack}>← Todos los consultorios</Button>}
      />

      <Async query={query}>
        {(data) => {
          const axisNamesOf = (inv: GalleryInvoice): Record<AxisKey, string[]> => ({
            receptor: inv.receptor_name ? [inv.receptor_name] : [],
            responsable: inv.responsables,
            pago: inv.pagadores.map((p) => p.name),
          });

          // Las opciones del filtro salen de los datos reales de este
          // consultorio — la unión de los tres ejes, no una lista inventada.
          const doctorOptions = Array.from(new Set(
            data.invoices.flatMap((inv) => Object.values(axisNamesOf(inv)).flat()),
          )).sort((a, b) => a.localeCompare(b));

          const activeAxes = AXES.filter((a) => axes[a.key]).map((a) => a.key);

          const matchesType = (inv: GalleryInvoice) =>
            typeFilter === 'ALL' ? true : typeFilter === 'NC' ? inv.is_credit_note : !inv.is_credit_note;

          /** null = sin dato en los ejes elegidos: no se afirma ni se oculta. */
          const matchesDoctor = (inv: GalleryInvoice): boolean | null => {
            if (!doctor) return true;
            const names = axisNamesOf(inv);
            const pool = activeAxes.flatMap((k) => names[k]);
            if (pool.length === 0) return null;
            return pool.includes(doctor);
          };

          const byType = data.invoices.filter(matchesType);
          const shown = byType.filter((inv) => matchesDoctor(inv) === true);
          const sinDato = doctor ? byType.filter((inv) => matchesDoctor(inv) === null) : [];

          // La estructura de la galería: meses (descendente) → sus facturas.
          // Una multi-mes aparece en cada mes que cubre (P2.2) con su marca.
          const vigentes = shown.filter((i) => !i.is_credit_note && !i.annulled_by);
          const months = Array.from(new Set(vigentes.flatMap((i) => i.periods)))
            .sort((a, b) => b.localeCompare(a));
          const noPeriod = vigentes.filter((i) => i.periods.length === 0);
          const annulled = shown.filter((i) => !i.is_credit_note && i.annulled_by);
          const creditNotes = shown.filter((i) => i.is_credit_note);

          const extras = {
            onView: (inv: InvoiceRow) => setViewing(inv),
            periodExtra: (inv: InvoiceRow) => {
              const g = inv as GalleryInvoice;
              return g.periods.length > 1 ? (
                // P2.2: la misma factura, compartida entre meses; el monto es
                // el del documento y en los totales cuenta una sola vez.
                <div>
                  <Badge tone="info" plain title={`Cubre ${g.periods.map(periodLabelShort).join(', ')}. El monto cuenta una sola vez en el total.`}>
                    misma factura para {g.periods.length} meses
                  </Badge>
                </div>
              ) : null;
            },
            docExtra: (inv: InvoiceRow) => {
              const g = inv as GalleryInvoice;
              return (
                <>
                  {g.annulled_by && (
                    <div><Badge tone="neutral" plain title={`Lo declara la nota …${g.annulled_by.slice(-4)} en su XML, no lo deduce el sistema.`}>anulada por NC …{g.annulled_by.slice(-4)}</Badge></div>
                  )}
                  {g.annuls && (
                    <div><Badge tone="ok" plain title={g.annul_reason
                      ? `Razón escrita en el XML de la nota: «${g.annul_reason}»`
                      : 'La nota nombra la factura anulada en su XML.'}>anula FE …{g.annuls.slice(-4)}</Badge></div>
                  )}
                  {g.review_pending && (
                    <div><Badge tone="warn" title="Esta nota se importó sin su XML, así que no dice qué factura anula. Lo decide una persona (CB-26).">pendiente de revisión</Badge></div>
                  )}
                  {g.suspect_by && (
                    <div><Badge tone="warn" plain title={`La nota …${g.suspect_by.slice(-4)} del mismo día y monto está sin resolver: no se afirma que este mes esté facturado.`}>NC sin resolver sobre este monto</Badge></div>
                  )}
                </>
              );
            },
            receptorExtra: (inv: InvoiceRow) => {
              const g = inv as GalleryInvoice;
              return (
                <>
                  {/* P2.1: la divergencia es una anomalía que se señala, no un
                      caso más. Premisa del dueño: receptor y responsable
                      «siempre deberían ser el mismo en principio». */}
                  {g.anomaly === 'DIFIERE' && (
                    <div><Badge tone="warn" title={`Responsable del período: ${g.responsables.join(', ') || '—'}`}>receptor ≠ responsable</Badge></div>
                  )}
                  {g.responsables.length > 0 && (
                    <div className="tbl__sub">Responsable: {g.responsables.join(', ')}</div>
                  )}
                  {g.pagadores.length > 0 && (
                    <div className="tbl__sub">
                      Pagó: {g.pagadores.map((p) => p.name).join(', ')}
                      {/* Sin `sender_name` histórico, lo que hay es a quién se
                          le REGISTRÓ el pago — se dice, no se disfraza. */}
                      {g.pagadores.every((p) => p.source === 'party') && ' (según registro)'}
                    </div>
                  )}
                </>
              );
            },
          };

          return (
            <>
              <div className="row row--wrap mb-3 invgal-filters">
                <label className="invgal-filters__field">
                  <span className="label">Doctor</span>
                  <select className="select" value={doctor} onChange={(e) => setDoctor(e.target.value)}>
                    <option value="">Todos</option>
                    {doctorOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>

                {/* P2.1: los tres ejes, seleccionables uno, todos o cada uno. */}
                <fieldset className="invgal-filters__axes" disabled={!doctor}>
                  <legend className="label">Buscarlo como</legend>
                  <div className="row row--wrap" style={{ gap: 12 }}>
                    {AXES.map((a) => (
                      <label key={a.key} className="row small" style={{ gap: 5 }}>
                        <input
                          type="checkbox"
                          checked={axes[a.key]}
                          onChange={(e) => setAxes((prev) => ({ ...prev, [a.key]: e.target.checked }))}
                        />
                        {a.label}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <span className="spacer" />

                <div
                  className="segmented"
                  role="group"
                  aria-label="Tipo de documento"
                  style={{ '--seg-n': 3, '--seg-i': typeFilter === 'ALL' ? 0 : typeFilter === 'FE' ? 1 : 2 } as React.CSSProperties}
                >
                  <div className="segmented__pill" />
                  {([['ALL', 'Todos'], ['FE', 'Facturas'], ['NC', 'Notas de crédito']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={typeFilter === key}
                      className={`segmented__opt${typeFilter === key ? ' is-active' : ''}`}
                      onClick={() => { if (typeFilter !== key) { haptic.light(); setTypeFilter(key); } }} // HAPTIC_FEEDBACK
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="row row--wrap mb-2" style={{ gap: 14 }}>
                <span className="small muted">
                  Total facturado vigente: <Money value={data.total} />
                  {data.invoices.some((i) => i.is_credit_note) && ' · las notas de crédito no se netean'}
                </span>
              </div>

              {doctor && activeAxes.length === 0 && (
                <Note tone="warn">Elegí al menos un eje para filtrar por doctor.</Note>
              )}

              {months.length === 0 && noPeriod.length === 0 && annulled.length === 0
                && creditNotes.length === 0 && sinDato.length === 0 ? (
                  <Empty icon="documento" title="Nada coincide con este filtro" hint="Probá con otro doctor, otro eje u otro tipo de documento." />
                ) : (
                  <div style={{ display: 'grid', gap: 14 }}>
                    {months.map((month) => {
                      const monthInvoices = vigentes.filter((i) => i.periods.includes(month));
                      const suspect = monthInvoices.find((i) => i.suspect_by);
                      return (
                        <Card
                          key={month}
                          title={periodLabel(month)}
                          actions={suspect && (
                            <Badge tone="warn" title={`La nota …${suspect.suspect_by!.slice(-4)} está pendiente de revisión: no se afirma que este mes esté facturado.`}>
                              NC pendiente de revisión
                            </Badge>
                          )}
                          flush
                        >
                          <InvoiceTable invoices={monthInvoices} {...extras} />
                        </Card>
                      );
                    })}

                    {noPeriod.length > 0 && (
                      <Card
                        title="Sin período identificado"
                        actions={<Badge tone="warn" plain>ni anuladas ni asignadas a un mes</Badge>}
                        flush
                      >
                        <InvoiceTable invoices={noPeriod} {...extras} />
                      </Card>
                    )}

                    {annulled.length > 0 && (
                      <Card
                        title="Anuladas por nota de crédito"
                        actions={<span className="small muted">corrección del mismo día — el mes lo cubre la refacturación</span>}
                        flush
                      >
                        <InvoiceTable invoices={annulled} {...extras} />
                      </Card>
                    )}

                    {creditNotes.length > 0 && (
                      <Card
                        title="Notas de crédito"
                        actions={<span className="small muted">jamás cuentan como pago ni egreso</span>}
                        flush
                      >
                        <InvoiceTable invoices={creditNotes} {...extras} />
                      </Card>
                    )}

                    {sinDato.length > 0 && (
                      // El filtro no inventa: donde no hay dato del eje, lo dice.
                      <Card
                        title={`Sin dato en los ejes seleccionados (${sinDato.length})`}
                        actions={<span className="small muted">no se puede afirmar si son de {doctor} o no</span>}
                        flush
                      >
                        <InvoiceTable invoices={sinDato} {...extras} />
                      </Card>
                    )}
                  </div>
                )}
            </>
          );
        }}
      </Async>

      {viewing && <InvoiceModal invoice={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

/* ── Las solicitudes del sistema (lo que esta pantalla era antes) ─────────── */

/**
 * `invoice_requests`: las facturas que el sistema le pide al emisor externo al
 * confirmar un pago. Hoy son 0, pero el flujo existe y el reintento de las
 * fallidas vive acá — se conserva como sección, ya sin fingir que es toda la
 * facturación.
 */
function SystemRequests() {
  const query = useApi<{ invoices: SystemInvoice[] }>('/admin/invoices');
  const missing = useApi<{ missing: { id: string; legal_name: string; display_name: string; phone: string | null }[]; count: number }>(
    '/admin/billing-profiles/missing-tax-id',
  );

  const retry = useApiMutation(
    (id: string) => api.post(`/admin/invoices/${id}/retry`),
    ['/admin/invoices'],
  );

  const requests = query.data?.invoices ?? [];

  return (
    <div className="mt-3" style={{ display: 'grid', gap: 14 }}>
      {(missing.data?.count ?? 0) > 0 && (
        <Note tone="warn">
          <strong>{missing.data!.count}</strong> pagadores no tienen cédula cargada. A esos no se les
          puede emitir factura electrónica: Hacienda exige identificar al receptor.
        </Note>
      )}

      {requests.length > 0 && (
        <Card title="Solicitudes al emisor externo" flush>
          <Table head={
            <tr>
              <th>Receptor</th><th>Período</th><th className="right">Monto</th>
              <th>Consecutivo</th><th>Estado</th><th />
            </tr>
          }>
            {requests.map((i) => (
              <tr key={i.id}>
                <td className="strong">
                  <Cell sub={i.unit_code}>{i.billing_name_snapshot}</Cell>
                </td>
                <td className="small muted">{periodLabel(i.period_start)}</td>
                <td className="right"><Money value={i.due_minor} /></td>
                <td className="small mono">
                  <Cell sub={i.issued_at ? dateLabel(i.issued_at.slice(0, 10)) : undefined}>
                    {i.consecutivo ?? '—'}
                  </Cell>
                </td>
                <td>
                  <StatusBadge value={i.status} labels={INVOICE_STATUS_LABEL} tones={INVOICE_STATUS_TONE} />
                  {i.error_message && <span className="tbl__sub">{i.error_message}</span>}
                </td>
                <td className="right">
                  {i.status === 'FAILED' && (
                    <Button disabled={retry.isPending} onClick={() => retry.mutate(i.id)}>
                      Reintentar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  );
}
